import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router';
import { useTranslation } from 'react-i18next';
import type { PeladaSummary } from '@shared/api';
import { createPelada, listPeladas } from '../../api/peladas';
import ImportLocalCard from './ImportLocalCard';
import { Button } from '../../components/ui';

// Home de quem está logado: as peladas da nuvem.
//
// O elenco local deixa de ser um segundo lugar onde guardar gente — vira só a
// origem da importação. Manter os dois vivos criaria duas fontes de verdade e a
// dúvida constante de onde os dados estão.
export default function PeladasPage() {
  const { t } = useTranslation();
  const [peladas, setPeladas] = useState<PeladaSummary[] | null>(null);
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  // Falhar em CARREGAR e falhar em CRIAR são dois erros, e antes dividiam um
  // estado só. O efeito era pior que a mensagem errada: a lista ficava `null`
  // para sempre, a tela dizia "Carregando…" sem fim, e o aviso aparecia 400px
  // abaixo, depois do formulário. Quem tem pressa lê uma tela travada.
  const [loadFailed, setLoadFailed] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoadFailed(false);
    const result = await listPeladas();
    if (result.ok) setPeladas(result.data.peladas);
    else setLoadFailed(true);
  }, []);

  // IIFE assíncrona em vez de `void load()`: assim o estado só muda depois do
  // await, nunca de forma síncrona no corpo do efeito.
  useEffect(() => {
    void (async () => {
      await load();
    })();
  }, [load]);

  async function handleCreate(e: React.FormEvent) {
    e.preventDefault();
    const trimmed = name.trim();
    if (!trimmed) return;

    setBusy(true);
    setError(null);
    const result = await createPelada(trimmed, 5);
    setBusy(false);

    if (result.ok) {
      setName('');
      await load();
    } else {
      setError(result.error);
    }
  }

  return (
    <div className="flex flex-col gap-6">
      <h1 className="font-display text-display">{t('cloud.myPeladas')}</h1>

      <ImportLocalCard onImported={() => void load()} />

      {loadFailed ? (
        <div
          role="alert"
          className="flex flex-col items-start gap-3 rounded-xl border border-dashed border-chalk-300 p-6 dark:border-pitch-600"
        >
          <p className="text-sm text-chalk-600 dark:text-chalk-300">{t('common.loadFailed')}</p>
          <Button tone="outline" size="sm" onClick={() => void load()}>
            {t('common.retry')}
          </Button>
        </div>
      ) : peladas === null ? (
        <p className="text-sm text-chalk-500 dark:text-chalk-400">{t('cloud.loading')}</p>
      ) : peladas.length === 0 ? (
        <p className="rounded-xl border border-dashed border-chalk-300 p-6 text-center text-sm text-chalk-500 dark:border-pitch-600 dark:text-chalk-400">
          {t('cloud.emptyPeladas')}
        </p>
      ) : (
        <ul className="flex flex-col gap-2">
          {peladas.map((p) => (
            <li key={p.id}>
              <Link
                to={`/pelada/${p.id}`}
                className="flex items-center justify-between gap-3 rounded-xl border border-chalk-200 bg-white p-4 transition-colors hover:bg-chalk-50 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-signal-focus dark:border-pitch-700 dark:bg-pitch-900 dark:hover:bg-pitch-800/60"
              >
                <span className="min-w-0">
                  <span className="block truncate font-semibold">{p.name}</span>
                  <span className="text-xs text-chalk-500 dark:text-chalk-400">
                    {t(`cloud.role.${p.role}`)} · {t('drawConfig.playersPerTeam')}: {p.playersPerTeam}
                  </span>
                </span>
                <span aria-hidden className="shrink-0 text-chalk-400">
                  →
                </span>
              </Link>
            </li>
          ))}
        </ul>
      )}

      <form
        onSubmit={handleCreate}
        className="flex flex-col gap-3 rounded-xl border border-chalk-200 bg-white p-5 sm:flex-row sm:items-end dark:border-pitch-700 dark:bg-pitch-900"
      >
        <div className="flex flex-1 flex-col gap-1.5">
          <label htmlFor="new-pelada" className="text-sm font-semibold">
            {t('cloud.newPelada')}
          </label>
          <input
            id="new-pelada"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder={t('cloud.newPeladaPlaceholder')}
            className="rounded-lg border border-chalk-200 bg-white px-3 py-2 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-signal-focus dark:border-pitch-600 dark:bg-pitch-950"
          />
        </div>
        <Button type="submit" disabled={busy || name.trim().length === 0} aria-busy={busy}>
          {busy ? t('common.sending') : t('common.create')}
        </Button>

        {/* O erro de criar mora DENTRO do formulário que falhou. Antes ficava
            depois dele, e num celular o aviso nascia fora da vista. */}
        {error && (
          <p role="alert" className="text-sm text-signal-danger sm:w-full">
            {t('cloud.errorGeneric')}
          </p>
        )}
      </form>

      {/* Também aqui, e não só na tela de quem não tem conta: o placar serve na
          quadra, e quem está na quadra já entrou na conta. */}
      <Link
        to="/placar"
        className="flex items-center justify-between gap-3 rounded-xl border border-chalk-200 bg-white p-4 transition-colors hover:border-turf-600 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-signal-focus dark:border-pitch-700 dark:bg-pitch-900 dark:hover:border-turf-400"
      >
        <span className="min-w-0">
          <span className="block font-semibold">{t('score.title')}</span>
          <span className="text-xs text-chalk-500 dark:text-chalk-400">
            {t('shell.homeScoreBody')}
          </span>
        </span>
        <span aria-hidden className="shrink-0 text-turf-600 dark:text-turf-400">
          →
        </span>
      </Link>
    </div>
  );
}
